import { useState } from "react";
import { Loader2, Lock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  DialogHeader,
  DialogTitle,
} from "@sportscanner/ui";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
} from "@/components/responsive-dialog";
import { apiDelete, getApiErrorCode } from "@/lib/api";
import { dateStrToEntry, formatCourtLabel } from "./helpers";

export interface BlockDetail {
  id: string;
  courtId: string;
  courtName: string;
  sportId: string;
  date: string;
  startTime: string;
  endTime: string;
  reason: string;
  seriesId: string | null;
  createdByUserName: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clubId: string;
  block: BlockDetail | null;
  onRemoved?: () => void;
}

export default function BlockRemoveDialog({
  open,
  onOpenChange,
  clubId,
  block,
  onRemoved,
}: Props) {
  const { t } = useTranslation();
  const [confirmSlot, setConfirmSlot] = useState(false);
  const [confirmSeries, setConfirmSeries] = useState(false);

  const removeSlot = useMutation({
    mutationFn: (id: string) =>
      apiDelete(`/api/admin/clubs/${clubId}/blocks/${id}`),
    onSuccess: () => {
      toast.success(t("blocks.remove.removedSlotToast"));
      setConfirmSlot(false);
      onOpenChange(false);
      onRemoved?.();
    },
    onError: (err: unknown) => {
      toast.error(t(`errors.api.${getApiErrorCode(err)}`));
    },
  });

  const removeSeries = useMutation({
    mutationFn: (seriesId: string) =>
      apiDelete<{ ok: true; deleted: number; fromDate: string }>(
        `/api/admin/clubs/${clubId}/blocks/series/${seriesId}`,
      ),
    onSuccess: (data) => {
      toast.success(
        t("blocks.remove.removedSeriesToast", { count: data.deleted }),
      );
      setConfirmSeries(false);
      onOpenChange(false);
      onRemoved?.();
    },
    onError: (err: unknown) => {
      toast.error(t(`errors.api.${getApiErrorCode(err)}`));
    },
  });

  const dateLabel = block
    ? (() => {
        const e = dateStrToEntry(block.date);
        return `${t(`days.long.${e.dayKey}`)}, ${e.date}. ${t(`months.${e.monthKey}`)}`;
      })()
    : "";

  return (
    <>
      <ResponsiveDialog open={open} onOpenChange={onOpenChange}>
        <ResponsiveDialogContent className="md:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-red-500" />
              {t("blocks.remove.title")}
            </DialogTitle>
          </DialogHeader>

          {block && (
            <div className="space-y-3">
              <div className="rounded-lg bg-red-50 border border-red-200 p-3 space-y-1.5 text-sm">
                <div className="font-medium text-neutral-800">
                  {dateLabel} · {block.startTime}–{block.endTime}
                </div>
                <div className="text-neutral-600">
                  {formatCourtLabel(block.courtName, t("blocks.remove.court"))}
                </div>
                <div className="text-neutral-600 italic">"{block.reason}"</div>
                {block.createdByUserName && (
                  <div className="text-xs text-neutral-400">
                    {t("blocks.remove.createdBy", {
                      name: block.createdByUserName,
                    })}
                  </div>
                )}
              </div>

              <div className="space-y-2 pt-1">
                <Button
                  onClick={() => setConfirmSlot(true)}
                  variant="outline"
                  className="w-full rounded-full text-red-600 border-red-200 hover:bg-red-50"
                >
                  {t("blocks.remove.removeSlot")}
                </Button>
                {block.seriesId && (
                  <Button
                    onClick={() => setConfirmSeries(true)}
                    variant="outline"
                    className="w-full rounded-full text-red-600 border-red-200 hover:bg-red-50"
                  >
                    {t("blocks.remove.removeSeries")}
                  </Button>
                )}
                <Button
                  onClick={() => onOpenChange(false)}
                  variant="outline"
                  className="w-full rounded-full"
                >
                  {t("blocks.remove.close")}
                </Button>
              </div>
            </div>
          )}
        </ResponsiveDialogContent>
      </ResponsiveDialog>

      <AlertDialog open={confirmSlot} onOpenChange={setConfirmSlot}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("blocks.remove.confirmSlotTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("blocks.remove.confirmSlotDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removeSlot.isPending}>
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={removeSlot.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (block) removeSlot.mutate(block.id);
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              {removeSlot.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                t("blocks.remove.removeSlot")
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmSeries} onOpenChange={setConfirmSeries}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("blocks.remove.confirmSeriesTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("blocks.remove.confirmSeriesDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removeSeries.isPending}>
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={removeSeries.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (block?.seriesId) removeSeries.mutate(block.seriesId);
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              {removeSeries.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                t("blocks.remove.removeSeries")
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
